import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Cell,
} from 'recharts';
import {
  Users, TrendingUp, Clock, MousePointerClick,
  FileDown, Activity, RefreshCw, AlertCircle, ExternalLink,
  CheckCircle, Eye, Globe,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  fetchLeadPortalAnalytics,
  fetchLeadsPortalActivity,
} from '../services/leadPortalAnalyticsService';
import { initiateIntegrationConnection, isIntegrationConfigured } from '../services/integrationService';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import {
  GLASS_PALETTE,
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassChip,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassTooltip,
  GlassEmpty,
  GlassDonut,
  GlassSearch,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

// ---------------------------------------------------------------------------
// Date range presets → GA4 expression strings
// ---------------------------------------------------------------------------
const DATE_PRESETS = [
  { label: 'Last 7 days',   startDate: '7daysAgo',  endDate: 'today' },
  { label: 'Last 30 days',  startDate: '30daysAgo', endDate: 'today' },
  { label: 'Last 90 days',  startDate: '90daysAgo', endDate: 'today' },
  { label: 'Last 6 months', startDate: '180daysAgo', endDate: 'today' },
  { label: 'This year',     startDate: 'yearAgo',   endDate: 'today' },
];

// Colour palette for charts
const CHART_COLORS = GLASS_PALETTE;

// Human-readable event names for display
const EVENT_LABELS = {
  scroll:           'Scroll depth',
  click:            'Outbound click',
  file_download:    'File download',
  form_start:       'Form started',
  form_submit:      'Form submitted',
  tab_click:        'Sidebar nav click',
  module_view:      'Module view',
  button_click:     'Button click',
  video_start:      'Video play',
  in_page_tab_click:'In-page tab click',
};

function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n));
}

function fmtDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d);
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ icon, label, value, sub }) {
  return (
    <div className={`${GLASS_INSET} px-4 py-3`}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-500 dark:text-white/[0.45]">{label}</span>
        {icon}
      </div>
      <div className="text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500 dark:text-white/40">{sub}</div>}
    </div>
  );
}

const KPI_ICON = 'h-4 w-4 text-gray-400 dark:text-white/40';

const SKELETON = 'animate-pulse rounded-2xl bg-black/[0.04] dark:bg-white/[0.05]';

function SetupBanner({ onConnect, message, permissionError, connectedEmail }) {
  const isConnectedNoAccess = permissionError || (message && /connected as/i.test(message));
  const title = isConnectedNoAccess
    ? 'Google Analytics connected — property access needed'
    : 'Connect Google Analytics to load charts';
  const defaultMessage = isConnectedNoAccess
    ? `Add ${connectedEmail || 'the Google account you used'} in GA4 → Admin → Property access management for property 481707605 (Viewer or higher), then click Refresh. Or reconnect with a different Google account.`
    : 'Sign in with a Google account that has Viewer access on GA4 property 481707605. Browser tracking (Measurement ID) works separately — this connection powers the report charts.';
  const toneText = isConnectedNoAccess ? GLASS_TEXT.blue : GLASS_TEXT.amber;

  return (
    <GlassCard className="mb-4 p-5" delay={0}>
      <div className="flex items-start gap-4">
        <span className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-full ${isConnectedNoAccess ? 'bg-[#5BC0EB]/20 dark:bg-[#5BC0EB]/[0.15]' : 'bg-[#FFB547]/20 dark:bg-[#FFB547]/[0.15]'} ${toneText}`}>
          {isConnectedNoAccess
            ? <CheckCircle size={18} />
            : <AlertCircle size={18} />}
        </span>
        <div className="flex-1">
          <p className="font-medium text-gray-900 dark:text-white">
            {title}
          </p>
          {connectedEmail && isConnectedNoAccess && (
            <p className={`mt-1 text-sm font-medium ${toneText}`}>
              Signed in as {connectedEmail}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-600 dark:text-white/60">
            {message || defaultMessage}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {onConnect && (
              <GlassAccentButton onClick={onConnect}>
                <Globe size={14} />
                {isConnectedNoAccess ? 'Reconnect Google Analytics' : 'Connect Google Analytics'}
              </GlassAccentButton>
            )}
            {isConnectedNoAccess && (
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={GLASS_SECONDARY_BUTTON}
              >
                Open GA4 property access <ExternalLink size={12} />
              </a>
            )}
            {!isConnectedNoAccess && (
              <a
                href="https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com?project=bba-portal-894c2"
                target="_blank"
                rel="noopener noreferrer"
                className={GLASS_SECONDARY_BUTTON}
              >
                Enable GA4 Data API <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const LeadPortalReports = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [datePreset, setDatePreset] = useState(1); // index into DATE_PRESETS
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(true);
  const [ga4Error, setGa4Error] = useState(null);

  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [showPortalOnly, setShowPortalOnly] = useState(false);

  const selectedPreset = DATE_PRESETS[datePreset];

  // Load GA4 data
  const loadGa4 = useCallback(async () => {
    setGa4Loading(true);
    setGa4Error(null);
    try {
      const data = await fetchLeadPortalAnalytics({
        startDate: selectedPreset.startDate,
        endDate: selectedPreset.endDate,
      });
      setGa4Data(data);
    } catch (err) {
      setGa4Error(err.message || 'Failed to load analytics');
    } finally {
      setGa4Loading(false);
    }
  }, [selectedPreset.startDate, selectedPreset.endDate]);

  // Load leads activity (once on mount)
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchLeadsPortalActivity();
        setLeadsData(data);
      } catch (err) {
        console.error('Error loading leads portal activity:', err);
      } finally {
        setLeadsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    loadGa4();
  }, [loadGa4]);

  const noGA4 = ga4Data?.configError;

  const handleConnectGa4 = () => {
    if (!isIntegrationConfigured('googleanalytics')) {
      setGa4Error('Google OAuth client is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env.');
      return;
    }
    initiateIntegrationConnection('googleanalytics');
  };

  // Prepare traffic sources for chart
  const sourcesChartData = useMemo(() => {
    if (!ga4Data?.sources) return [];
    const grouped = {};
    ga4Data.sources.forEach(row => {
      const key = row.source === '(direct)' ? 'Direct' : row.source || 'Unknown';
      grouped[key] = (grouped[key] || 0) + (row.sessions || 0);
    });
    return Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [ga4Data]);

  // Prepare events for chart
  const eventsChartData = useMemo(() => {
    if (!ga4Data?.events) return [];
    return ga4Data.events.map(row => ({
      name: EVENT_LABELS[row.eventName] || row.eventName,
      count: row.eventCount || 0,
    }));
  }, [ga4Data]);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    let list = leadsData;
    if (showPortalOnly) list = list.filter(l => l.portalUserId);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(l =>
        l.company?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      // Leads with portal users first, then by last login recency
      if (a.portalUserId && !b.portalUserId) return -1;
      if (!a.portalUserId && b.portalUserId) return 1;
      if (a.lastLogin && b.lastLogin) return new Date(b.lastLogin) - new Date(a.lastLogin);
      return 0;
    });
  }, [leadsData, search, showPortalOnly]);

  const leadsWithPortal = leadsData.filter(l => l.portalUserId).length;
  const leadsLoggedIn = leadsData.filter(l => l.lastLogin).length;

  const chart = glassChartTheme(darkMode);
  const sourcesDonutData = sourcesChartData.map((d, i) => ({ ...d, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const sourcesTotal = sourcesChartData.reduce((sum, d) => sum + d.value, 0);
  const presetOptions = DATE_PRESETS.map((p, i) => ({ value: i, label: p.label }));

  const stats = [
    { icon: Users, value: fmt(leadsData.length), label: 'Total leads' },
    {
      icon: CheckCircle,
      value: fmt(leadsWithPortal),
      label: 'Portal signups',
      badge: leadsData.length ? `${Math.round(leadsWithPortal / leadsData.length * 100)}% of leads` : undefined,
      badgeTone: 'mint',
    },
    { icon: Activity, value: fmt(leadsLoggedIn), label: 'Logged in' },
    { icon: FileDown, value: fmt(leadsData.filter(l => l.freeResourceClaimed).length), label: 'Free resource claimed' },
  ];

  const muted = 'text-gray-500 dark:text-white/50';

  return (
    <GlassPage className="text-gray-900 dark:text-white" backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate(-1)}
        title={<>Lead Portal<br />Reports</>}
        stats={stats}
        controls={(
          <>
            <span className="text-sm font-medium text-gray-600 dark:text-white/60">Date range:</span>
            <SlidingPills size="sm" options={presetOptions} value={datePreset} onChange={setDatePreset} ariaLabel="Date range" />
            <button
              type="button"
              onClick={loadGa4}
              disabled={ga4Loading}
              className={GLASS_SECONDARY_BUTTON}
            >
              <RefreshCw size={14} className={ga4Loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </>
        )}
      />
      <p className={`-mt-4 mb-4 text-sm ${muted}`}>
        Start portal traffic, engagement events, and per-lead activity
      </p>

      {/* Setup Banner */}
      {noGA4 && !ga4Loading && (
        <SetupBanner
          onConnect={handleConnectGa4}
          message={ga4Data?.message}
          permissionError={ga4Data?.permissionError}
          connectedEmail={ga4Data?.connectedEmail}
        />
      )}

      {/* GA4 error */}
      {ga4Error && !noGA4 && (
        <div className={`mb-4 rounded-2xl border border-[#F80707]/20 bg-[#F80707]/[0.06] p-4 text-sm dark:border-[#F80707]/30 dark:bg-[#F80707]/10 ${GLASS_TEXT.red}`}>
          <strong>Analytics error:</strong> {ga4Error}
        </div>
      )}

      <div className="space-y-4">
        {/* ── Overview KPIs ── */}
        <PinnableCard id="start-portal-overview" title="Start Portal Overview" size="wide">
        <GlassCard className="p-5" delay={0}>
          <GlassCardHeader title="Start Portal Overview" className="mb-4" />
          {ga4Loading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className={`${SKELETON} h-20`} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
              <KpiCard icon={<Users className={KPI_ICON} />} label="Active users" value={fmt(ga4Data?.overview?.activeUsers)} />
              <KpiCard icon={<Users className={KPI_ICON} />} label="New users" value={fmt(ga4Data?.overview?.newUsers)} />
              <KpiCard icon={<Activity className={KPI_ICON} />} label="Sessions" value={fmt(ga4Data?.overview?.sessions)} />
              <KpiCard icon={<Eye className={KPI_ICON} />} label="Page views" value={fmt(ga4Data?.overview?.pageViews)} />
              <KpiCard icon={<Clock className={KPI_ICON} />} label="Avg session" value={fmtDuration(ga4Data?.overview?.avgSessionDuration)} />
              <KpiCard icon={<TrendingUp className={KPI_ICON} />} label="Engagement" value={fmtPct(ga4Data?.overview?.engagementRate)} />
              <KpiCard icon={<MousePointerClick className={KPI_ICON} />} label="Bounce rate" value={fmtPct(ga4Data?.overview?.bounceRate)} />
            </div>
          )}
        </GlassCard>
        </PinnableCard>

        {/* ── Traffic sources + Engagement side by side ── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

          {/* Traffic Sources */}
          <PinnableCard id="traffic-sources" title="Traffic Sources">
          <GlassCard className="p-5" delay={80}>
            <GlassCardHeader title="Traffic Sources" className="mb-4" />
            {ga4Loading ? (
              <div className={`${SKELETON} h-56`} />
            ) : sourcesChartData.length === 0 ? (
              <GlassEmpty className="h-32">No source data yet.</GlassEmpty>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="h-[180px]">
                  <GlassDonut data={sourcesDonutData} dataKey="value" nameKey="name" inner={78} outer={88} darkMode={darkMode} unit="sessions">
                    <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{fmt(sourcesTotal)}</div>
                    <div className="text-xs text-gray-500 dark:text-white/[0.45]">sessions</div>
                  </GlassDonut>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {sourcesDonutData.map((d) => (
                    <GlassChip
                      key={d.name}
                      color={d.color}
                      label={d.name}
                      value={`${sourcesTotal ? ((d.value / sourcesTotal) * 100).toFixed(0) : 0}%`}
                    />
                  ))}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className={GLASS_TH}>Source</th>
                        <th className={`${GLASS_TH} text-right`}>Sessions</th>
                        <th className={`${GLASS_TH} pr-0 text-right`}>Users</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-white/75">
                      {(ga4Data?.sources || []).slice(0, 8).map((row, i) => (
                        <tr key={i} className={GLASS_ROW}>
                          <td className="py-3 pr-4">
                            <span className="font-medium text-gray-900 dark:text-white">{row.source || '(direct)'}</span>
                            {row.medium && row.medium !== '(none)' && (
                              <span className="ml-1 text-xs text-gray-500 dark:text-white/40">/ {row.medium}</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">{fmt(row.sessions)}</td>
                          <td className="py-3 text-right tabular-nums">{fmt(row.activeUsers)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </GlassCard>
          </PinnableCard>

          {/* Engagement Events */}
          <PinnableCard id="engagement-events" title="Engagement Events">
          <GlassCard className="p-5" delay={140}>
            <GlassCardHeader title="Engagement Events" className="mb-4" />
            {ga4Loading ? (
              <div className={`${SKELETON} h-56`} />
            ) : eventsChartData.length === 0 ? (
              <GlassEmpty className="h-32">No event data yet — tracking will populate once users interact with the Start portal.</GlassEmpty>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={eventsChartData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
                  <CartesianGrid horizontal={false} stroke={chart.grid} strokeDasharray="4 6" />
                  <XAxis type="number" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: chart.cursor }} content={<GlassTooltip />} />
                  <Bar dataKey="count" name="Events" radius={[0, 12, 12, 0]} maxBarSize={30} background={{ fill: chart.track, radius: 12 }} animationDuration={700}>
                    {eventsChartData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[(i + 1) % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </GlassCard>
          </PinnableCard>
        </div>

        {/* ── Top Start Pages ── */}
        <PinnableCard id="top-start-pages" title="Top Start Pages" size="wide">
        <GlassCard className="p-5" delay={200}>
          <GlassCardHeader
            title="Top Start Pages"
            className="mb-4"
            right={<Globe size={16} className="text-gray-400 dark:text-white/40" />}
          />
          {ga4Loading ? (
            <div className={`${SKELETON} h-48`} />
          ) : !ga4Data?.pages?.length ? (
            <GlassEmpty className="h-32">No page data yet.</GlassEmpty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={GLASS_TH}>Page path</th>
                    <th className={`${GLASS_TH} hidden sm:table-cell`}>Title</th>
                    <th className={`${GLASS_TH} text-right`}>Views</th>
                    <th className={`${GLASS_TH} pr-0 text-right`}>Users</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-white/75">
                  {ga4Data.pages.map((row, i) => (
                    <tr key={i} className={GLASS_ROW}>
                      <td className={`py-3 pr-4 font-mono text-xs ${GLASS_TEXT.mint}`}>{row.pagePath}</td>
                      <td className={`hidden py-3 pr-4 sm:table-cell ${muted}`}>{row.pageTitle || '—'}</td>
                      <td className="py-3 pr-4 text-right tabular-nums">{fmt(row.pageViews)}</td>
                      <td className="py-3 text-right tabular-nums">{fmt(row.activeUsers)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
        </PinnableCard>

        {/* ── Leads Activity ── */}
        <PinnableCard id="leads-portal-activity" title="Leads Portal Activity" size="wide">
        <GlassCard className="p-5" delay={260}>
          <GlassCardHeader
            title="Leads Portal Activity"
            right={leadsLoading ? <BBALoader size={20} /> : null}
          />
          <p className={`mt-1 text-xs ${muted}`}>
            {leadsWithPortal} of {leadsData.length} leads have signed up for the Start portal · {leadsLoggedIn} have logged in
          </p>

          {/* Search / filter bar */}
          <div className="mb-4 mt-4 flex flex-wrap items-center gap-3">
            <GlassSearch
              className="min-w-[200px] flex-1"
              placeholder="Search by company or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-gray-700 dark:text-white/70">
              <input
                type="checkbox"
                checked={showPortalOnly}
                onChange={e => setShowPortalOnly(e.target.checked)}
                className="h-4 w-4 rounded accent-[#F80707]"
              />
              Portal signups only
            </label>
          </div>

          {/* Leads table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Company / Lead</th>
                  <th className={`${GLASS_TH} hidden md:table-cell`}>Email</th>
                  <th className={GLASS_TH}>Portal signup</th>
                  <th className={`${GLASS_TH} hidden sm:table-cell`}>Last login</th>
                  <th className={`${GLASS_TH} hidden text-center lg:table-cell`}>Logins</th>
                  <th className={`${GLASS_TH} hidden text-center lg:table-cell`}>Step</th>
                  <th className={`${GLASS_TH} pr-0 text-center`}>Resource</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-white/75">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={`py-8 text-center text-sm ${muted}`}>
                      {leadsLoading ? <div className="flex justify-center"><BBALoader size={32} /></div> : 'No leads found.'}
                    </td>
                  </tr>
                ) : filteredLeads.map(lead => (
                  <tr key={lead.accountId} className={GLASS_ROW}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <GlassAvatar name={lead.company || ''} />
                        <a
                          href={`/crm/lead/${lead.accountId}`}
                          className="font-medium text-gray-900 transition hover:text-[#F80707] hover:underline dark:text-white dark:hover:text-[#FF4D40]"
                        >
                          {lead.company || '(unnamed)'}
                        </a>
                      </div>
                    </td>
                    <td className={`hidden py-3 pr-4 md:table-cell ${muted}`}>
                      {lead.email || '—'}
                    </td>
                    <td className="py-3 pr-4">
                      {lead.portalUserId ? (
                        <GlassBadge tone="mint">
                          {fmtDate(lead.portalSignupDate) !== '—' ? fmtDate(lead.portalSignupDate) : 'Signed up'}
                        </GlassBadge>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-white/30">Not signed up</span>
                      )}
                    </td>
                    <td className="hidden whitespace-nowrap py-3 pr-4 sm:table-cell">
                      {fmtDate(lead.lastLogin)}
                    </td>
                    <td className="hidden py-3 pr-4 text-center tabular-nums lg:table-cell">
                      {lead.loginCount > 0 ? lead.loginCount : '—'}
                    </td>
                    <td className="hidden py-3 pr-4 text-center tabular-nums lg:table-cell">
                      {lead.startFlowStep != null ? lead.startFlowStep : '—'}
                    </td>
                    <td className="py-3 text-center">
                      {lead.freeResourceClaimed ? (
                        <CheckCircle size={14} className={`mx-auto ${GLASS_TEXT.mint}`} />
                      ) : (
                        <span className="text-xs text-gray-300 dark:text-white/20">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLeads.length > 0 && (
            <p className="mt-4 text-xs text-gray-400 dark:text-white/30">
              Showing {filteredLeads.length} of {leadsData.length} leads
            </p>
          )}
        </GlassCard>
        </PinnableCard>
      </div>
    </GlassPage>
  );
};

export default LeadPortalReports;
